import React, { useEffect } from 'react';
import SEO from '../../components/SEO';

const PrivacyPage = () => {
    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white text-gray-800 font-sans">
            <SEO
                title="Privacy Policy"
                description="Privacy Policy for gio billing. Learn how we collect, use, store, and protect your business data."
                url="/privacy-policy"
            />

            {/* Theme Green Header Banner */}
            <div className="bg-[#047857] text-white py-12 px-6 md:px-12 relative overflow-hidden">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 relative z-10">
                    <div className="space-y-1">
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Privacy Policy</h1>
                        <p className="text-sm text-white/80 font-medium">Latest update: 24 June 2024</p>
                    </div>
                    <div>
                        <a 
                            href="/" 
                            className="inline-flex items-center justify-center bg-white text-[#047857] px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-50 active:scale-95 transition-all shadow-md shadow-black/5"
                        >
                            Go to Home
                        </a>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
                <div className="prose prose-slate max-w-none text-gray-700 leading-relaxed text-[16px] space-y-6">
                    <p className="text-[16px] leading-[1.8] text-gray-700">
                        At gio billing ("we," "our," "us"), we value your privacy and are committed to protecting your personal information. This Privacy Policy outlines our practices regarding the collection, use, disclosure, and protection of your data when you visit our website <a href="https://giobilling.com" className="text-gray-900 font-semibold underline hover:text-[#047857] transition-colors">giobilling.com</a>. By using our services, you agree to the terms of this policy. Please read it carefully to understand how we handle your information.
                    </p>

                    {/* Section 1 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            1. Introduction
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            gio billing is dedicated to creating a safe and secure online environment for our clients and website visitors. We understand the importance of privacy and are committed to safeguarding your personal information. This Privacy Policy explains our practices concerning the collection, use, and sharing of your data. We encourage you to review this policy regularly as we may update it periodically to reflect changes in our practices or legal requirements.
                        </p>
                    </div>

                    {/* Section 2 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            2. Information We Collect
                        </h2>
                        
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-bold text-gray-950 mb-2">
                                    2.1. Personal Data
                                </h3>
                                <p className="text-[16px] leading-[1.8] text-gray-700">
                                    We collect personally identifiable information ("Personal Data") that you voluntarily provide to us. This may include your name, email address, phone number, postal address, and billing information. You may provide this information when you sign up for our services, contact us for inquiries, or participate in surveys and promotions. We also collect details about your preferences and interests to tailor our services to your needs.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-gray-950 mb-2">
                                    2.2. Non-Personal Data
                                </h3>
                                <p className="text-[16px] leading-[1.8] text-gray-700">
                                    In addition to Personal Data, we collect non-personal information ("Non-Personal Data") that cannot be used to identify you individually. This includes data such as your IP address, browser type, operating system, referring URLs, pages viewed, and the dates/times of your visits. Non-Personal Data helps us understand user behavior and improve our website's performance and user experience. We may also collect aggregated data from multiple users, which is not linked to any specific individual.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 3 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            3. How We Use Your Information
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-bold text-gray-950 mb-3">
                                    3.1. Personal Data
                                </h3>
                                <p className="text-[16px] leading-[1.8] text-gray-700 mb-4">
                                    We use the Personal Data we collect for various purposes, including:
                                </p>
                                <ul className="space-y-3.5 text-[16px] leading-[1.8] text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <span className="text-gray-400 mt-1">•</span>
                                        <span>
                                            <strong className="text-gray-950">Providing Services:</strong> To deliver the services you request, manage your account, and process transactions.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-gray-400 mt-1">•</span>
                                        <span>
                                            <strong className="text-gray-950">Communication:</strong> To communicate with you about your account, respond to inquiries, provide updates, and send promotional materials.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-gray-400 mt-1">•</span>
                                        <span>
                                            <strong className="text-gray-950">Personalization:</strong> To tailor our services to your preferences and enhance your user experience.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-gray-400 mt-1">•</span>
                                        <span>
                                            <strong className="text-gray-950">Improvement:</strong> To analyze and improve our services, develop new features, and understand how users interact with our website.
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPage;
